import styled from 'styled-components';
import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';
import {
  InfoCircleFilled,
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';

export type AlertCardType = 'info' | 'success' | 'warning' | 'error';

export interface AlertCardProps {
  /** Type of alert */
  type?: AlertCardType;
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Whether to show the close button */
  closable?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Custom icon */
  icon?: React.ReactNode;
  /** An action shown at the end of the card, e.g. a Cancel button */
  action?: React.ReactNode;
}

const getAlertStyles = (token: GlobalToken, type: AlertCardType) => {
  const styles = {
    info: {
      icon: token.colorInfo,
      background: token.colorInfoBg,
      border: token.colorInfo,
    },
    success: {
      icon: token.colorSuccess,
      background: token.colorSuccessBg,
      border: token.colorSuccess,
    },
    warning: {
      icon: token.colorWarning,
      background: token.colorWarningBg,
      border: token.colorWarning,
    },
    error: {
      icon: token.colorError,
      background: token.colorErrorBg,
      border: token.colorError,
    },
  };
  return styles[type];
};

const iconComponents = {
  info: InfoCircleFilled,
  success: CheckCircleFilled,
  warning: ExclamationCircleFilled,
  error: CloseCircleFilled,
};

interface StyledProps {
  $token: GlobalToken;
  $type: AlertCardType;
}

const CardWrapper = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXS}px;
  padding: ${({ $token }) => $token.paddingSM}px;
  border-radius: ${radius.box}px;
  border: 1.5px solid ${({ $token, $type }) => getAlertStyles($token, $type).border};
  background: ${({ $token, $type }) => getAlertStyles($token, $type).background};
  min-width: 215px;
`;

const IconWrapper = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .anticon {
    font-size: 20px;
    color: ${({ $token, $type }) => getAlertStyles($token, $type).icon};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const Title = styled.span<{ $token: GlobalToken }>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  color: ${({ $token }) => $token.colorTextBase};
  line-height: 1.4;
`;

const Description = styled.span<{ $token: GlobalToken }>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM - 2}px;
  font-weight: 400;
  color: ${({ $token }) => $token.colorTextBase};
  line-height: 1.4;
`;

const CloseButton = styled.button<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity ${({ $token }) => $token.motionDurationMid} ease;

  .anticon {
    font-size: 16px;
    color: ${({ $token, $type }) => getAlertStyles($token, $type).icon};
  }

  &:hover {
    opacity: 0.7;
  }
`;

/**
 * AlertCard component based on Figma Design System
 *
 * A compact alert card for displaying info, success, warning, or error messages.
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { AlertCard } from '@libertify/ui';
 *
 * <AlertCard
 *   type="info"
 *   title="Information"
 *   description="This is an informational message."
 * />
 *
 * <AlertCard
 *   type="error"
 *   title="Error"
 *   description="Something went wrong."
 *   closable
 *   onClose={() => console.log('closed')}
 * />
 * ```
 */
export const AlertCard = ({
  type = 'info',
  title,
  description,
  closable = false,
  onClose,
  icon,
  action,
}: AlertCardProps) => {
  const { token } = theme.useToken();
  const IconComponent = iconComponents[type];

  return (
    <CardWrapper $token={token} $type={type}>
      <IconWrapper $token={token} $type={type}>
        {icon || <IconComponent />}
      </IconWrapper>
      <Content>
        <Title $token={token}>{title}</Title>
        {description && <Description $token={token}>{description}</Description>}
      </Content>
      {action}
      {closable && (
        <CloseButton
          $token={token}
          $type={type}
          onClick={onClose}
          aria-label="Close alert"
        >
          <CloseOutlined />
        </CloseButton>
      )}
    </CardWrapper>
  );
};

export default AlertCard;
